import {
    flatten,
    map,
    pathOr,
    length,
    equals,
    T,
    cond,
    always,
    keys,
    join,
    filter,
    includes,
    all,
    replace,
    match,
    trim,
    dissoc,
    compose,
    startsWith,
    uniq,
    split,
    gt
} from "ramda";
import { mergeObjects, mergeWithArrayConcat } from "src/utilities/arrayObjects";
import { filterIsNotEmpty, isNotEmpty, returnIsNotEmptyOrTrue } from "src/utilities/hasValue";
import { isString, isObject, isArray } from "src/utilities/types";

export interface QueryData {
    name: string;
    query: string;
    variables: any;
    hasConditionalExp?: boolean;
    tableName?: string;
}

const linebreak = `

`

export const QUERY_OPERATION_GET = 'query'
export const QUERY_OPERATION_MUTATION = 'mutation'

/**
 * gqlQueryAligner used to align graphql with proper spacing on query to parse it
 * @param query { string } query string to be parsed ex:- 'acccounts(where:{    object_meta:{_contains:$object}} { id,email,first_name})'
 * @returns aligned query string with proper spaceing 'acccounts(where: { object_meta: {_contains: $object}} { id, email, first_name})'
 */
export const gqlQueryAligner = (query: string) => {
    return compose(
        replace(/\s+/g, ' '),
        replace(/:/g, ': '),
        replace(/,/g, ', '),
        trim
    )(query)
}


/**
 * getWordsStartingWithDollor used to get all word which starts with $ symbol
 * @param inputString { string } query string ex:- 'acccounts(where:{    object_meta:{_contains:$object}, id:{_eq: $accountId}} { id,email,first_name})'
 * @returns list of strings which starts with $ ex:- ["$object", "$accountId"]
 */
const getWordsStartingWithDollor = (inputString: string) => {
    const regex = /\$[\w]*/g;
    const matches = inputString.match(regex);
    return matches || []
}

/**
 * getGraphqlSetVariable used to get only the setter variable in the query string
 * @param input  { string } query string ex:- update_borrowers(where:{object_meta:{_contains: $objectMeta}}, _set: $borrower){
 * @returns setter variable name ex:- "$borrower"
 */
const getGraphqlSetVariable = (input: string) => {
    const targetIndex = split('_set:', input);
    if (gt(targetIndex.length, 1)) {
        return pathOr('', [0], getWordsStartingWithDollor(pathOr('', [1], targetIndex)))
    }
    return '';
}

const insertRegex = /insert[_][\w]*/g;

const updateRegex = /update[_][\w]*/g;

class NumberString {
    value: string;
    constructor(value: string) {
        this.value = value
    }

    get() {
        return this.value
    }
}

class NumberStringArray {
    values: NumberString[];
    constructor(values: string[]) {
        this.values = values.map((v: string) => new NumberString(v))
    }

    get() {
        return this.values.map((v: NumberString) => v.get())
    }
}


class QueryEvaluator {
    _query: string;
    _variables: any;
    _name: string;
    _hasExpression: boolean;
    _tableName: string;

    constructor(queryData: QueryData) {
        this._variables = pathOr({}, ['variables'], queryData)
        this._query = this._alignQuery(pathOr('', ['query'], queryData))
        this._name = pathOr('', ['name'], queryData)
        this._hasExpression = pathOr(false, ['hasConditionalExp'], queryData)
        this._tableName = pathOr('', ['tableName'], queryData)
    }


    _alignQuery(query: string) {
        return gqlQueryAligner(query)
    }

    name() {
        return this._name
    }

    query() {
        return this._query
    }

    variables() {
        return map((value) => {
            return (isNumberString(value) || isNumberStringArray(value)) ? value.get() : value
        }, this._variables)
    }

    private __variables() {
        return this._variables
    }

    isValid() {
        // // TODO: have to validate with graphql functions parseValue, parse
        const variables = keys(this.__variables())
        const query = replace(/\n/g, " ", this.query())
        return equals(length(this.queryVariables()), length(variables)) &&
            all((variable: string) => includes(`$${variable}`, query), variables)
    }

    queryVariables() {
        return getWordsStartingWithDollor(this.query());
    }

    insertMutationObject() {
        // Used this to scrap text alike 'insert_<tablename>' from mutation query
        // so we can get table name
        const matches = match(insertRegex, this.query());
        const insertTable = pathOr('', [0], matches)
        const tableName = replace('insert_', '', insertTable)
        const isArray = includes('objects:', this.query())
        const inputVariable = `$${pathOr('', [0], keys(this.__variables()))}`

        return {
            tableName,
            inputVariable,
            mutationType: returnIsNotEmptyOrTrue(insertTable, `${tableName}_insert_input`, ''),
            isArray
        }
    }

    updateMutationObject() {
        // Used this to scrap text alike 'update_<tablename>' from mutation query
        // so we can get table name
        const matches = match(updateRegex, this.query());

        const insertTable = pathOr('', [0], matches)
        const tableName = replace('update_', '', insertTable)
        const inputVariable = getGraphqlSetVariable(this.query())
        const data = {
            tableName,
            inputVariable,
            mutationType: returnIsNotEmptyOrTrue(insertTable, `${tableName}_set_input`, '')
        }
        return data
    }

    mutationData() {
        return cond([
            [() => startsWith('insert_', this.query()), always(this.insertMutationObject())],
            [() => startsWith('update_', this.query()), always(this.updateMutationObject())],
            [T, always({})]
        ])()
    }

    buildVariableString() {
        const variables = this.__variables()
        const { mutationType, isArray, inputVariable = '' } = this.mutationData()
        const variableType = isArray && mutationType ? `[${mutationType}]` : mutationType
        const mainVar = isNotEmpty(inputVariable) && isNotEmpty(variableType) ? `${inputVariable}: ${variableType}!` : ''
        const variableKeys = filterIsNotEmpty(keys(dissoc(inputVariable.replace(/[^\w_]/g, ''), variables)))
        const variableStrings = map(key => {
            // TODO instead of if condition to check different type use NumberString class modal
            if (this._hasExpression) {
                const graphqlExpressionType = `${this._tableName}_bool_exp`
                const expressionType = isObjectArray(pathOr('', [key], variables)) ? `[${graphqlExpressionType}!]` : graphqlExpressionType
                return `$${key}: ${expressionType}`
            }
            const value = pathOr('', [key], variables)
            const variableType = isNotEmpty(value) ? getGraphqlType(value) : '';
            return isNotEmpty(key) && isNotEmpty(value) ?
                `$${key}: ${variableType}` : '';
        }, variableKeys)

        return flatten([mainVar, variableStrings])
    }
}

type GBuilder = 'query' | 'mutation'


class MutationBuilder {
    queryEvals: QueryEvaluator[];
    functionName?: string
    name: string = QUERY_OPERATION_MUTATION;
    constructor(queryEvals: QueryEvaluator[], functionName?: string) {
        this.queryEvals = queryEvals
        this.functionName = functionName
    }


    buildQueryFunctions() {
        const queries = filter(isNotEmpty, flatten([map((q: QueryEvaluator) => q.query(), this.queryEvals)])
        )
        return join(linebreak, queries)
    }

    mergeVeriables() {
        return mergeWithArrayConcat(
            flatten([map((q: QueryEvaluator) => q.variables(), this.queryEvals)])
        )
    }

    buildVariableString() {
        const variableStrings = flatten(map((q) => {
            const varStrings = q.buildVariableString()
            return varStrings
        }, this.queryEvals))
        return join(', ', filterIsNotEmpty(uniq(variableStrings)))
    }


    buildQuery() {
        const functionName = isNotEmpty(this.functionName) ? this.functionName : 'gql_mutation_builder'
        const query = `${this.name} ${functionName}(${this.buildVariableString()}) {
            ${this.buildQueryFunctions()}
        }`
        return query
    }


}

class QueryBuilder {
    queryEvals: QueryEvaluator[];
    functionName?: string
    name: string = QUERY_OPERATION_GET;
    constructor(queryEvals: QueryEvaluator[], functionName?: string) {
        this.queryEvals = queryEvals
        this.functionName = functionName
    }

    buildQueryFunctions() {
        const queries = filter(isNotEmpty, flatten([map((q: QueryEvaluator) => q.query(), this.queryEvals)])
        )
        return join(linebreak, queries)
    }

    mergeVeriables() {
        return mergeObjects(
            flatten([map((q: QueryEvaluator) => q.variables(), this.queryEvals)])
        )
    }

    buildVariableString() {
        const variableStrings = flatten(map((q) => {
            const varStrings = q.buildVariableString()
            return varStrings
        }, this.queryEvals))
        return join(', ', filterIsNotEmpty(uniq(variableStrings)))
    }

    buildQuery() {
        const functionName = isNotEmpty(this.functionName) ? this.functionName : 'gql_query_builder'
        const query = `${this.name} ${functionName}(${this.buildVariableString()}) {
            ${this.buildQueryFunctions()}
        }`
        return query
    }

}

class GQLQueryBuilder {
    queryEvals: QueryEvaluator[];
    builderFn: (q: QueryEvaluator[]) => any;
    builder: any;
    constructor(
        queryData: QueryData[],
        functionName?: string,
        builder: GBuilder = QUERY_OPERATION_GET) {
        this.queryEvals = map((q) => {
            return new QueryEvaluator(q)
        }, queryData)
        this.builderFn = equals(builder, QUERY_OPERATION_MUTATION) ?
            (qEvals) => new MutationBuilder(qEvals, functionName) :
            (qEvals) => new QueryBuilder(qEvals, functionName);
        this.builder = this.builderFn(this.queryEvals)
    }

    isValid() {
        return all((v: QueryEvaluator) => v.isValid())(this.queryEvals)
    }

    getBuilder(names: string[]) {
        const qEvals = filter((q: QueryEvaluator) => {
            return includes(q.name(), names)
        }, this.queryEvals)
        return this.builderFn(qEvals)
    }

    getQueryString() {
        return this.builder.buildQuery()
    }

    getQueryVariables() {
        return this.builder.mergeVeriables()
    }

    getVariablesByName(names: string[]) {
        return this.getBuilder(names).mergeVeriables()
    }

    getQuery() {
        return {
            query: this.getQueryString(),
            variables: this.getQueryVariables()
        }
    }

    getQueryByName(names: string[]) {
        const builder = this.getBuilder(names)
        return {
            query: builder.buildQuery(),
            variables: builder.mergeVeriables()
        }
    }

    getQueryNames() {
        return map((q: QueryEvaluator) => q.name(), this.queryEvals)
    }
}

const DB_UID_LENGTH = 19

const isBigInt = (v: any) => {
    if (typeof v === "string" && equals(length(v as any) == DB_UID_LENGTH)) {
        return !isNaN(v as any);
    }
    return false
}

const isStringsArray = (arr: any) => isArray(arr) && arr.every(isString)

const isBigIntArray = (arr: any) => isArray(arr) && arr.every(isBigInt)

const isObjectArray = (arr: any) => isArray(arr) && arr.every(isObject)

const isNumberString = (value: any) => value instanceof NumberString

const isNumberStringArray = (value: any) => value instanceof NumberStringArray

const getGraphqlType = (value: any) => {
    return cond([
        [isBigInt, always('bigint')],
        [isBigIntArray, always('[bigint]!')],
        [isStringsArray, always('[String]')],
        [isNumberStringArray, always('[String]')],
        [isNumberString, always('String')],
        [isObject, always('jsonb')],
        [isString, always('String')],
        [T, always('jsonb')]
    ])(value)
}



export {
    GQLQueryBuilder,
    NumberString,
    NumberStringArray
}